// ============================================
// PersonaCard
// Reusable persona card for ArchetypeLibrary and PatientBuilder
// Expand/collapse with lab details, medications, validation checks
// NO framer-motion — pure CSS, matches working admin page patterns
// ============================================

import { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  FlaskConical,
  Pill,
  AlertTriangle,
  CheckCircle2,
  Circle,
  Bot,
  ShieldAlert,
} from 'lucide-react';

// ── Types ──

export interface LabValue {
  biomarker: string;
  value: number;
  unit: string;
  conventionalRange: string;
  functionalRange: string;
  status: string;
  trigger: boolean;
}

export interface Medication {
  name: string;
  dose: string;
  category: string;
  duration: string;
}

export interface ValidationCheck {
  checkId: string;
  check: string;
  category: string;
  severity: string;
  automatable: boolean;
}

export interface PersonaData {
  personaId: string;
  name: string;
  archetype: string;
  demographics: { name: string; age: number; sex: string; state: string };
  labs: LabValue[];
  medications: Medication[];
  conditions: string[];
  contraindications: string[];
  allergens?: string[];
  validationChecks: ValidationCheck[];
}

export interface PersonaCardProps {
  persona: PersonaData;
  lastPassRate?: number;
  onRunTwin?: () => void;
}

// ── Status color mapping ──

const STATUS_COLORS: Record<string, { text: string; bg: string; label: string }> = {
  below_conventional: { text: 'text-critical', bg: 'bg-critical/10', label: 'Below Conv.' },
  below_functional: { text: 'text-amber-400', bg: 'bg-amber-400/10', label: 'Below Func.' },
  optimal: { text: 'text-optimal', bg: 'bg-optimal/10', label: 'Optimal' },
  above_functional: { text: 'text-amber-400', bg: 'bg-amber-400/10', label: 'Above Func.' },
  above_conventional: { text: 'text-critical', bg: 'bg-critical/10', label: 'Above Conv.' },
};

// ── Archetype badge colors ──

const ARCHETYPE_STYLES: Record<string, { text: string; bg: string; border: string }> = {
  'Clean Gate 1': { text: 'text-emerald-400', bg: 'bg-emerald-400/10', border: 'border-emerald-400/30' },
  'Drug Interactions': { text: 'text-critical', bg: 'bg-critical/10', border: 'border-critical/30' },
  'The Skeptic': { text: 'text-purple-400', bg: 'bg-purple-400/10', border: 'border-purple-400/30' },
  'Polypharmacy': { text: 'text-orange-400', bg: 'bg-orange-400/10', border: 'border-orange-400/30' },
  'Extreme Values': { text: 'text-red-400', bg: 'bg-red-400/10', border: 'border-red-400/30' },
  'Allergy Heavy': { text: 'text-yellow-400', bg: 'bg-yellow-400/10', border: 'border-yellow-400/30' },
  'Elderly': { text: 'text-blue-400', bg: 'bg-blue-400/10', border: 'border-blue-400/30' },
  'Minimal Labs': { text: 'text-gray-400', bg: 'bg-gray-400/10', border: 'border-gray-400/30' },
};

function getArchetypeStyle(archetype: string) {
  return ARCHETYPE_STYLES[archetype] || { text: 'text-gold', bg: 'bg-gold/10', border: 'border-gold/30' };
}

// ── Severity badge colors ──

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'text-critical bg-critical/10',
  major: 'text-amber-400 bg-amber-400/10',
  minor: 'text-blue-400 bg-blue-400/10',
};

const CATEGORY_COLORS: Record<string, string> = {
  clinical: 'text-critical',
  compliance: 'text-amber-400',
  conversational: 'text-purple-400',
};

// ── Component ──

export function PersonaCard({ persona, lastPassRate }: PersonaCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());

  const archetypeStyle = getArchetypeStyle(persona.archetype);
  const medCount = persona.medications.length;
  const labCount = persona.labs.length;

  function toggleCheck(checkId: string) {
    setCheckedItems((prev) => {
      const next = new Set(prev);
      if (next.has(checkId)) next.delete(checkId);
      else next.add(checkId);
      return next;
    });
  }

  return (
    <div className="bg-bg-card border border-bg-tertiary rounded-xl overflow-hidden">
      {/* ── Collapsed Header ── */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 p-5 hover:bg-bg-elevated/50 transition-colors text-left"
      >
        {/* Avatar */}
        <div className="w-11 h-11 rounded-full bg-bg-elevated border border-bg-tertiary flex items-center justify-center flex-shrink-0">
          <span className="text-text-muted text-base font-semibold">
            {persona.name.split(' ').map((n) => n[0]).join('')}
          </span>
        </div>

        {/* Name + archetype */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-base font-semibold text-text-primary truncate">
              {persona.name}
            </span>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${archetypeStyle.text} ${archetypeStyle.bg} ${archetypeStyle.border}`}>
              {persona.archetype}
            </span>
          </div>
          <p className="text-sm text-text-muted mt-1">
            {persona.demographics.age}y {persona.demographics.sex} | {persona.demographics.state}
          </p>
        </div>

        {/* Badges */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="flex items-center gap-1.5 text-sm text-text-secondary">
            <FlaskConical size={14} className="text-gold" />
            <span>{labCount}</span>
          </div>
          <div className="flex items-center gap-1.5 text-sm text-text-secondary">
            <Pill size={14} className={medCount > 0 ? 'text-amber-400' : 'text-text-muted'} />
            <span>{medCount}</span>
          </div>
          {lastPassRate !== undefined ? (
            <span className={`text-sm font-semibold px-2 py-0.5 rounded ${
              lastPassRate >= 90 ? 'text-emerald-400 bg-emerald-400/10'
                : lastPassRate >= 70 ? 'text-amber-400 bg-amber-400/10'
                : 'text-critical bg-critical/10'
            }`}>
              {lastPassRate}%
            </span>
          ) : (
            <span className="text-xs text-text-muted px-2 py-0.5 rounded bg-bg-elevated">Not tested</span>
          )}
          <div className="text-text-muted">
            {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </div>
        </div>
      </button>

      {/* ── Expanded Detail — simple conditional, no framer-motion ── */}
      {expanded && (
        <div className="border-t border-bg-tertiary px-5 py-5 space-y-6">
          {/* Lab Values Table */}
          <div>
            <h4 className="text-base font-display text-gold mb-3 flex items-center gap-2">
              <FlaskConical size={16} />
              Lab Values
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-base">
                <thead>
                  <tr className="border-b border-bg-tertiary">
                    <th className="text-left py-2 pr-4 text-text-muted font-medium text-sm">Biomarker</th>
                    <th className="text-right py-2 px-4 text-text-muted font-medium text-sm">Value</th>
                    <th className="text-left py-2 px-4 text-text-muted font-medium text-sm">Unit</th>
                    <th className="text-left py-2 px-4 text-text-muted font-medium text-sm">Conv. Range</th>
                    <th className="text-left py-2 px-4 text-text-muted font-medium text-sm">Func. Range</th>
                    <th className="text-left py-2 pl-4 text-text-muted font-medium text-sm">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {persona.labs.map((lab) => {
                    const sc = STATUS_COLORS[lab.status] || { text: 'text-text-muted', bg: 'bg-bg-elevated', label: lab.status };
                    return (
                      <tr key={lab.biomarker} className="border-b border-bg-tertiary/50 last:border-0">
                        <td className="py-2.5 pr-4 text-text-primary font-medium">
                          {lab.biomarker}
                          {lab.trigger && <AlertTriangle size={12} className="inline ml-1.5 text-amber-400" />}
                        </td>
                        <td className={`py-2.5 px-4 text-right font-semibold ${sc.text}`}>{lab.value}</td>
                        <td className="py-2.5 px-4 text-text-muted">{lab.unit}</td>
                        <td className="py-2.5 px-4 text-text-secondary">{lab.conventionalRange}</td>
                        <td className="py-2.5 px-4 text-text-secondary">{lab.functionalRange}</td>
                        <td className="py-2.5 pl-4">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded ${sc.text} ${sc.bg}`}>{sc.label}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Medications */}
          {persona.medications.length > 0 && (
            <div>
              <h4 className="text-base font-display text-gold mb-3 flex items-center gap-2">
                <Pill size={16} />
                Medications
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {persona.medications.map((med) => (
                  <div key={med.name} className="bg-bg-elevated rounded-lg border border-bg-tertiary px-4 py-3">
                    <div className="text-base font-semibold text-text-primary">{med.name}</div>
                    <div className="text-sm text-text-secondary mt-0.5">{med.dose} &middot; {med.category}</div>
                    <div className="text-sm text-text-muted mt-0.5">Duration: {med.duration}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Contraindications */}
          {persona.contraindications.length > 0 && (
            <div>
              <h4 className="text-base font-display text-amber-400 mb-3 flex items-center gap-2">
                <ShieldAlert size={16} />
                Contraindications
              </h4>
              <div className="flex flex-wrap gap-2">
                {persona.contraindications.map((ci) => (
                  <span key={ci} className="text-sm px-3 py-1 rounded-lg bg-critical/10 text-critical border border-critical/20">{ci}</span>
                ))}
              </div>
            </div>
          )}

          {/* Conditions */}
          <div>
            <h4 className="text-base font-display text-gold mb-3">Conditions</h4>
            <div className="flex flex-wrap gap-2">
              {persona.conditions.map((c) => (
                <span key={c} className="text-sm px-3 py-1 rounded-lg bg-bg-elevated text-text-secondary border border-bg-tertiary">{c}</span>
              ))}
            </div>
          </div>

          {/* Validation Checklist */}
          <div>
            <h4 className="text-base font-display text-gold mb-3 flex items-center gap-2">
              <CheckCircle2 size={16} />
              Validation Checklist
              <span className="text-sm font-sans text-text-muted ml-2">
                {checkedItems.size}/{persona.validationChecks.length}
              </span>
            </h4>
            <div className="space-y-2">
              {persona.validationChecks.map((vc) => {
                const isChecked = checkedItems.has(vc.checkId);
                const severityClass = SEVERITY_COLORS[vc.severity] || 'text-text-muted bg-bg-elevated';
                const categoryColor = CATEGORY_COLORS[vc.category] || 'text-text-muted';
                return (
                  <button
                    key={vc.checkId}
                    onClick={() => toggleCheck(vc.checkId)}
                    className={`w-full text-left flex items-center gap-3 px-4 py-3 rounded-lg border transition-colors ${
                      isChecked
                        ? 'bg-emerald-400/5 border-emerald-400/20'
                        : 'bg-bg-elevated border-bg-tertiary hover:border-gold/20'
                    }`}
                  >
                    {isChecked ? (
                      <CheckCircle2 size={18} className="text-emerald-400 flex-shrink-0" />
                    ) : (
                      <Circle size={18} className="text-text-muted flex-shrink-0" />
                    )}
                    <span className={`flex-1 text-base ${isChecked ? 'text-text-muted line-through' : 'text-text-primary'}`}>
                      {vc.check}
                    </span>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`text-xs font-medium ${categoryColor}`}>{vc.category}</span>
                      <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${severityClass}`}>{vc.severity}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Action Row */}
          <div className="flex items-center justify-between pt-2 border-t border-bg-tertiary">
            <div className="text-sm text-text-muted">
              {persona.validationChecks.length} validation checks &middot;{' '}
              {persona.validationChecks.filter((v) => v.severity === 'critical').length} critical
            </div>
            <div className="relative group">
              <button
                disabled
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gold/20 text-gold font-medium text-base cursor-not-allowed opacity-60"
              >
                <Bot size={16} />
                Run AI Twin
              </button>
              <div className="absolute bottom-full right-0 mb-2 px-3 py-1.5 rounded-lg bg-bg-elevated border border-bg-tertiary text-sm text-text-muted whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                Coming Soon
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
